import { Tween } from "@westpierstudio/luma/createjs";
import { View } from "@westpierstudio/luma/mvc";
import { GameServices } from "game/GameServices";
import { SpinModeType } from "@westpierstudio/core-module-oryx/components/ui/slot-game-ui/UIStateMachine";
import { Spine } from "pixi-spine";
import { VSOption } from "servertypes/SharedTypes";
import { Container, Sprite } from "pixi.js";
import { CreateComplexText } from "utils/ComplexText";
import { ImplementBoundedContainer } from "utils/BoundedContainer";

const enum State {
    INIT,
    IDLE,
    SPIN,
    ACTIVE,
    TRANSITION,
    BONUS
};

type MessageSet = {
    messages: [Container, Container],
    nextIndex: 0 | 1
}

export class ReelTopDisplay extends View {
    private readonly dragonsMessageContainer: Container;
    private readonly pandasMessageContainer: Container;
    private readonly dragonsLogoContainer: Container;
    private readonly pandasLogoContainer: Container;
    private readonly dragonLogo: Sprite;
    private readonly pandaLogo: Sprite;
    private readonly vsEffect: Spine & Container;
    private readonly vsEffectPandaExtra: Spine & Container;
    private readonly messageData: {dragons: MessageSet, pandas: MessageSet};
    private state = State.INIT;
    private timer = 0;
    private spinMode = SpinModeType.MANUAL;
    private spinModeChanged = false;

    private featuringMessage: Container;
    private jumpingMessage: Container;
    private combinationMessage: Container;

    private hiddenContainer: Container;

    constructor(protected override readonly services: GameServices) {
        super(services);

        const {factory, resources} = this.services;

        const spec = resources.getSpec(`reeltopdisplay`);
        factory.applySpec(this, spec);

        this.vsEffect = factory.createSpine(spec.vsEffect, this);
        this.vsEffectPandaExtra = factory.createSpine(spec.vsEffect, this);
        this.vsEffectPandaExtra.x = spec.vsEffect.pandaPos2.x;
        this.vsEffectPandaExtra.y = spec.vsEffect.pandaPos2.y;
        this.vsEffectPandaExtra.scale = spec.vsEffect.pandaPos2.scale;

        this.dragonsMessageContainer = factory.createContainer(spec.reelTopDisplayDragonsMessageContainer, this);
        this.pandasMessageContainer = factory.createContainer(spec.reelTopDisplayPandasMessageContainer, this);

        this.dragonsLogoContainer = factory.createContainer({}, this);
        this.pandasLogoContainer = factory.createContainer({}, this);

        this.dragonLogo = factory.createSprite(spec.dragonLogo, this.dragonsLogoContainer);
        this.pandaLogo = factory.createSprite(spec.pandaLogo, this.pandasLogoContainer);

        this.hiddenContainer = factory.createContainer({}, this);

        this.featuringMessage = CreateComplexText(services, resources.getMessage(`reelTopDisplayFeaturingComplex`),
                                                  [spec.templates.whiteText, spec.templates.yellowText],
                                                  [],
                                                  spec.messageChunkData.features, this.hiddenContainer, spec.messageChunkData.newLineSpacing);
        factory.applySpec(this.featuringMessage, spec.messageLayouts.features);
        this.featuringMessage = ImplementBoundedContainer(this.featuringMessage, spec.messageBounds.features, true);

        this.jumpingMessage = CreateComplexText(services, resources.getMessage(`reelTopDisplayJumpingComplex`),
                                                [spec.templates.whiteText, spec.templates.yellowText],
                                                [spec.messageIcons.wild],
                                                spec.messageChunkData.jumping, this.hiddenContainer, spec.messageChunkData.newLineSpacing);
        factory.applySpec(this.jumpingMessage, spec.messageLayouts.jumping);
        this.jumpingMessage = ImplementBoundedContainer(this.jumpingMessage, spec.messageBounds.jumping, true);

        this.combinationMessage = CreateComplexText(services, resources.getMessage(`reelTopDisplayCombinationComplex`),
                                                    [spec.templates.whiteText, spec.templates.yellowText],
                                                    [spec.messageIcons.blank, spec.messageIcons.vs, spec.messageIcons.fg],
                                                    spec.messageChunkData.combination, this.hiddenContainer, spec.messageChunkData.newLineSpacing);
        factory.applySpec(this.combinationMessage, spec.messageLayouts.combination);
        this.combinationMessage = ImplementBoundedContainer(this.combinationMessage, spec.messageBounds.combination, true);

        this.messageData = {
            dragons: {
                messages: [this.featuringMessage, this.combinationMessage],
                nextIndex: 0
            },
            pandas: {
                messages: [this.jumpingMessage, this.featuringMessage],
                nextIndex: 0
            }
        };

        this.hiddenContainer.visible = false;

        this.disableVSEffects();
        this.setCurrentMessagesAndUpdateIndex();
        this.setBaseGameMode();
    }

    setSpin(): void {

        //Clear the transition if it's either a manual spin or the 1st auto spin of a set
        if (this.state === State.TRANSITION && (this.spinMode === SpinModeType.MANUAL || this.spinModeChanged)) {

            Tween.removeTweens(this.dragonsMessageContainer);
            Tween.removeTweens(this.pandasMessageContainer);

            this.dragonsMessageContainer.alpha = 1;
            this.pandasMessageContainer.alpha = 1;

            this.setCurrentMessagesAndUpdateIndex();
        }

        this.state = State.SPIN;
        this.spinModeChanged = false;
    }


    //called on both forced auto stop and at the end of an auto set
    setSpinModeChanged(spinMode: SpinModeType): void {

        this.spinModeChanged = this.spinMode !== spinMode;
        this.spinMode = spinMode;

        this.timer = 0;
    }

    setIdle(): void {

        if (this.spinMode === SpinModeType.MANUAL) {

            this.state = State.IDLE;
            this.timer = 0;
        }
    }

    setBaseGameMode(): void {

        this.dragonsMessageContainer.visible = true;
        this.pandasMessageContainer.visible = true;

        this.dragonsLogoContainer.visible = false;
        this.pandasLogoContainer.visible = false;
    }

    setBonusGameMode(): void {

        this.dragonsMessageContainer.visible = false;
        this.pandasMessageContainer.visible = false;

        this.dragonsLogoContainer.visible = true;
        this.pandasLogoContainer.visible = true;
        this.dragonsLogoContainer.alpha = 1;
        this.pandasLogoContainer.alpha = 1;

        this.state = State.BONUS;
    }

    setVSEffects(vsPickIndex: VSOption): void {

        const {resources} = this.services;
        let spec;

        this.vsEffect.visible = true;

        if (vsPickIndex === VSOption.DRAGON) {
            spec = resources.getSpec(`reeltopdisplay`).vsEffect.dragonPos;

            this.vsEffect.state.setAnimation(0, `dragon-explosions`, true);
        } else {
            spec = resources.getSpec(`reeltopdisplay`).vsEffect.pandaPos1;

            this.vsEffect.state.setAnimation(0, `panda-eletricity`, true);
            this.vsEffectPandaExtra.state.setAnimation(0, `panda-eletricity`, true);

            this.vsEffectPandaExtra.visible = true;
        }

        this.vsEffect.x = spec.x;
        this.vsEffect.y = spec.y;
        this.vsEffect.scale = spec.scale;
    }

    disableVSEffects(): void {
        this.vsEffect.visible = false;
        this.vsEffectPandaExtra.visible = false;
    }

    fadeLogos(): void {
        const {logoFadeTime} = this.services.resources.getSpec(`reeltopdisplay`);

        Tween.get(this.dragonsLogoContainer).to({alpha: 0}, logoFadeTime);
        Tween.get(this.pandasLogoContainer).to({alpha: 0}, logoFadeTime);

        this.vsEffect.visible = false;
        this.vsEffectPandaExtra.visible = false;
    }

    /**
     *
     * @param dt Seconds
    */
    tick(dt: number): void {

        const {idleTime, autoSpinIdleTime, holdTime} = this.services.resources.getSetting(`reelTopDisplay`);

        if (this.state === State.INIT) {
            return;
        }

        this.timer += (dt * 1000);

        if (this.state !== State.BONUS) {

            if (this.spinMode === SpinModeType.AUTO) {

                if (this.timer > autoSpinIdleTime) {

                    this.timer = 0;
                    this.cycleMessages();
                }
            } else if (this.state === State.IDLE) {

                if (this.timer >= idleTime) {

                    this.state = State.ACTIVE;
                    this.timer = 0;
                    this.cycleMessages();
                }
            } else if (this.state === State.ACTIVE) {

                if (this.timer >= holdTime) {

                    this.timer = 0;
                    this.cycleMessages();
                }
            }
        }
    }

    override reset(): void {

        this.messageData.dragons.nextIndex = 0;
        this.messageData.pandas.nextIndex = 0;
        this.state = State.INIT;
        this.timer = 0;
        this.spinMode = SpinModeType.MANUAL;
        this.spinModeChanged = false;

        Tween.removeTweens(this.dragonsMessageContainer);
        Tween.removeTweens(this.pandasMessageContainer);

        this.dragonsMessageContainer.alpha = 1;
        this.pandasMessageContainer.alpha = 1;

        this.setCurrentMessagesAndUpdateIndex();
    }

    private cycleMessages(): void {

        const {fadeTime} = this.services.resources.getSetting(`reelTopDisplay`);

        const previousState = this.state;
        this.state = State.TRANSITION;

        Tween.get(this.dragonsMessageContainer)
        .to({alpha: 0}, fadeTime)
        .call(() => {
            this.addNextMessage(this.dragonsMessageContainer, this.messageData.dragons);
        })
        .to({alpha: 1}, fadeTime);

        Tween.get(this.pandasMessageContainer)
        .to({alpha: 0}, fadeTime)
        .call(() => {
            this.addNextMessage(this.pandasMessageContainer, this.messageData.pandas);
        })
        .to({alpha: 1}, fadeTime)
        .call(() => {

            this.setNextMessageIndex(this.messageData.dragons);
            this.setNextMessageIndex(this.messageData.pandas);

            this.state = previousState;
        });
    }

    private setCurrentMessagesAndUpdateIndex(): void {

        this.addNextMessage(this.dragonsMessageContainer, this.messageData.dragons);
        this.addNextMessage(this.pandasMessageContainer, this.messageData.pandas);

        this.setNextMessageIndex(this.messageData.dragons);
        this.setNextMessageIndex(this.messageData.pandas);
    }

    private addNextMessage(container: Container, set: MessageSet): void {

        container.removeChildren();
        container.addChild(set.messages[set.nextIndex]);
    }

    private setNextMessageIndex(set: MessageSet): void {
        set.nextIndex = (set.nextIndex === 0) ? 1 : 0;
    }
}